/**
 * @file        src/pages/flight/RaiseFlightAmendmentsModal.jsx
 * @description The "Amendments" popup (GoParyatak RaiseFlightAmendments), opened
 *              from the Booking Details drawer's Cart Amendments card. Shows the
 *              read-only Booking Id, the 14-option Amendment Type dropdown and a
 *              Raise button. Behaviour by group:
 *                • email   (9 types) → POST /amendment/email (records + notifies ops)
 *                • cancel  (Cancellation) → hands off to the existing cancel flow
 *                • screen/reissue (Ssr, Full Refund, Void, Re-Issue) → not wired in
 *                  this panel yet (Phase 2); shown but clearly marked so nothing
 *                  silently no-ops.
 *              Special rule: a CANCELLED booking may only raise a Correction.
 * @author      Kinetika Labs
 * @created     2026-07-19
 * @changelog
 *   - 2026-07-19 Claude — Phase 2: Void + Full Refund now open the preview→confirm
 *                          action modal (group 'refund'); only Ssr + Re-Issue remain
 *                          deferred.
 *   - 2026-07-19 Claude — Initial (Phase 1: popup + 9 email types + wire Cancel).
 *   - 2026-08-10 Claude — Amendment Type now uses the themed Select (console-wide
 *                          dropdown consistency pass) instead of a native <select>.
 *   - 2026-09-15 Claude — API errors read via apiErrorMessage(): the error handler returns {error:{code,message}}, and putting that object into state/toast threw "Objects are not valid as a React child", blanking the whole app.
 */
import { useMemo, useState } from 'react';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import { toast } from '../../lib/toast';
import { raiseEmailAmendment } from '../../api/flightBookings';
import { apiErrorMessage } from '../../api/client';

// The 14 amendment types, in the reference order. `group` decides the behaviour.
const AMENDMENT_OPTIONS = [
  { value: 'SSR', label: 'Special Service Request (Seat / Meal / Baggage)', group: 'screen' },
  { value: 'CANCELLATION', label: 'Cancellation', group: 'cancel' },
  { value: 'FULL_REFUND', label: 'Full Refund', group: 'refund' },
  { value: 'VOID', label: 'Void', group: 'refund' },
  { value: 'REISSUE', label: 'Re-Issue', group: 'reissue' },
  { value: 'FARE_CHANGE', label: 'Fare Change', group: 'email' },
  { value: 'MISCELLANEOUS', label: 'Miscellaneous', group: 'email' },
  { value: 'CANCELLATION_QUOTATION', label: 'Cancellation Quotation', group: 'email' },
  { value: 'REISSUE_QUOTATION', label: 'Reissue Quotation', group: 'email' },
  { value: 'NO_SHOW', label: 'No Show', group: 'email' },
  { value: 'CORRECTION', label: 'Correction', group: 'email' },
  { value: 'CUSTOM', label: 'Custom', group: 'email' },
  { value: 'ADD_INFANT', label: 'Add Infant', group: 'email' },
  { value: 'INSURANCE_CANCELLATION', label: 'Insurance Cancellation', group: 'email' },
];

const DEFERRED_LABEL = {
  screen: 'SSR (add seat / meal / baggage) runs a supplier selection + payment flow that is not available in this panel yet.',
  reissue: 'Re-Issue runs the multi-step reschedule + payment flow, not available in this panel yet.',
};

export default function RaiseFlightAmendmentsModal({ booking, onClose, onCancellation, onAction, onRaised }) {
  const isCancelled = /cancel/i.test(booking?.booking_status || '');

  // A cancelled booking may raise ONLY a Correction (reference rule).
  const options = useMemo(
    () => (isCancelled ? AMENDMENT_OPTIONS.filter((o) => o.value === 'CORRECTION') : AMENDMENT_OPTIONS),
    [isCancelled],
  );

  const [type, setType] = useState(options[0]?.value || 'CORRECTION');
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const selected = options.find((o) => o.value === type) || options[0];
  const deferred = selected?.group === 'screen' || selected?.group === 'reissue';

  const raise = async () => {
    setErr('');
    if (!selected) return;

    // Cancellation → hand off to the existing preview→confirm cancel flow.
    if (selected.group === 'cancel') {
      onClose();
      onCancellation?.(booking);
      return;
    }

    // Void / Full Refund → hand off to the preview→confirm action modal.
    if (selected.group === 'refund') {
      onClose();
      onAction?.(selected.value, booking);
      return;
    }

    // Not wired in this panel yet — never pretend it did something.
    if (deferred) {
      setErr(DEFERRED_LABEL[selected.group] || 'This amendment type is not available yet.');
      return;
    }

    // Email-only types → record + notify ops.
    setBusy(true);
    try {
      const res = await raiseEmailAmendment(booking.id, { type: selected.value, remarks: remarks.trim() });
      const d = res?.data || {};
      if (d.emailed) {
        toast.ok('Amendment raised', `${selected.label} recorded and the ops team was emailed.`);
      } else {
        toast.ok('Amendment recorded', `${selected.label} was logged for the ops team (email notification unavailable).`);
      }
      onRaised?.();
      onClose();
    } catch (e) {
      setErr(apiErrorMessage(e, 'Could not raise the amendment.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 480 }}>
        <div className="lk-modal-head">
          <h3>Amendments · #{booking.id}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>

        <div className="det-body">
          <div className="field full">
            <label>Booking Id</label>
            <input value={booking.booking_reference || ''} readOnly disabled />
          </div>

          <div className="field full">
            <label>Amendment Type</label>
            <Select
              value={type}
              onChange={(v) => { setType(v); setErr(''); }}
              options={options}
              disabled={busy}
            />
          </div>

          {isCancelled && (
            <p className="muted" style={{ fontSize: '.85em' }}>
              This booking is cancelled, so only a Correction amendment can be raised.
            </p>
          )}

          {(selected?.group === 'cancel' || selected?.group === 'refund') && (
            <p className="muted" style={{ fontSize: '.85em' }}>
              {selected.label} opens a refund preview so you can review the amount from the airline before confirming.
            </p>
          )}

          {selected?.group === 'email' && (
            <div className="field full">
              <label>Remarks {selected.value === 'CUSTOM' ? '(required detail for a custom request)' : '(optional)'}</label>
              <textarea
                rows={3}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Add any detail the ops team needs to action this."
                disabled={busy}
                maxLength={1000}
              />
            </div>
          )}

          {deferred && (
            <p className="muted" style={{ fontSize: '.85em' }}>
              {DEFERRED_LABEL[selected.group]}
            </p>
          )}

          {err ? <div className="field-error">{err}</div> : null}

          <div className="form-actions">
            <button className="btn ghost" onClick={onClose} disabled={busy}>Close</button>
            <button
              className="btn primary"
              onClick={raise}
              disabled={busy || deferred}
            >
              {busy ? 'Raising…'
                : selected?.group === 'cancel' ? 'Continue to cancel'
                : selected?.group === 'refund' ? 'Continue'
                : 'Raise'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
